import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Affix,
  Button,
  Card,
  Col,
  Grid,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Typography,
  message,
} from "antd";
import { ArrowLeftOutlined, SaveOutlined, MinusCircleOutlined, PrinterOutlined } from "@ant-design/icons";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency, toApiNumber } from "../../../utils/formatters";
import { fetchSystemConfig, readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { fetchExchangeRates, EXCHANGE_RATES_UPDATED_EVENT } from "../../../shared/lib/exchangeRates";
import { PurchaseOrderPrintSheet } from "../components/PurchaseOrderPrintSheet";
import "../../../shared/styles/ListPages.css";
import "./PurchaseOrderEditor.css";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const newLineKey = () => `l-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const toMoney = (value) => Math.round(toApiNumber(value, 0) * 100) / 100;

const PurchaseOrderEditorPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id;
  const screens = useBreakpoint();
  const sideBySide = Boolean(screens.lg);

  const [loadingPo, setLoadingPo] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [systemConfig, setSystemConfig] = useState(() => readCachedSystemConfig());
  const [exchangeRates, setExchangeRates] = useState([]);
  const [vendors, setVendors] = useState([]);

  const [vendorId, setVendorId] = useState(undefined);
  const [reference, setReference] = useState("");
  const [status, setStatus] = useState("draft");

  const [lines, setLines] = useState([]);

  const [amountPaid, setAmountPaid] = useState(0);
  const [amountPaidMain, setAmountPaidMain] = useState(0);
  const [amountPaidSecondary, setAmountPaidSecondary] = useState(0);
  const [exchangeRateId, setExchangeRateId] = useState(null);

  const [searchOptions, setSearchOptions] = useState([]);
  const [searchFetching, setSearchFetching] = useState(false);
  const searchTimerRef = useRef(null);
  const [productSelectKey, setProductSelectKey] = useState(0);
  const [poCreatedAt, setPoCreatedAt] = useState(null);

  const mainCode = systemConfig?.currency_code || "IQD";
  const secondaryCode = systemConfig?.secondary_currency_code || "";
  const useDualCurrency = Boolean(secondaryCode);

  const getTranslatedName = useCallback(
    (nameInput) => {
      let nameTranslations;
      try {
        nameTranslations = typeof nameInput === "string" ? JSON.parse(nameInput) : nameInput;
      } catch {
        return typeof nameInput === "string" ? nameInput : "—";
      }
      return (
        nameTranslations?.[i18n.language] ||
        nameTranslations?.en ||
        nameTranslations?.ku ||
        nameTranslations?.ar ||
        "—"
      );
    },
    [i18n.language]
  );

  const formatProductLabel = useCallback(
    (p) => {
      const name = getTranslatedName(p.name);
      const sku = p.sku || "";
      const bc = p.barcode ? ` · ${p.barcode}` : "";
      return `${sku}${bc} — ${name}`;
    },
    [getTranslatedName]
  );

  const lineTotals = useMemo(() => {
    let cents = 0;
    lines.forEach((row) => {
      cents += Math.round(toApiNumber(row.quantity, 0) * toApiNumber(row.single_price, 0) * 100);
    });
    return cents / 100;
  }, [lines]);

  const selectedRate = useMemo(() => {
    if (!exchangeRateId || !exchangeRates.length) return null;
    return exchangeRates.find((r) => Number(r.id) === Number(exchangeRateId)) || null;
  }, [exchangeRateId, exchangeRates]);

  const appliedMainTotal = useMemo(() => {
    const main = toApiNumber(amountPaidMain, 0);
    const sec = toApiNumber(amountPaidSecondary, 0);
    if (!useDualCurrency) {
      return toApiNumber(amountPaid, 0);
    }
    const secondaryPerMain = selectedRate ? toApiNumber(selectedRate.main_per_secondary, 0) : 0;
    if (!secondaryPerMain) return Math.round(main * 100) / 100;
    return Math.round((main + sec / secondaryPerMain) * 100) / 100;
  }, [amountPaid, amountPaidMain, amountPaidSecondary, selectedRate, useDualCurrency]);

  const vendorDisplayName = useMemo(() => {
    const v = vendors.find((x) => Number(x.id) === Number(vendorId));
    return v?.name || "—";
  }, [vendors, vendorId]);

  const canPrint = Boolean(vendorId) && lines.some((r) => r.product_id != null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await fetchSystemConfig();
        if (cancelled) return;
        setSystemConfig(cfg);
        if (!cfg?.secondary_currency_code) {
          setExchangeRates([]);
          setExchangeRateId(null);
          return;
        }
        const rates = await fetchExchangeRates();
        if (cancelled) return;
        setExchangeRates(rates);
        setExchangeRateId((prev) =>
          prev && rates.some((r) => Number(r.id) === Number(prev)) ? prev : rates[0]?.id ?? null
        );
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onFxUpdated = () => {
      (async () => {
        try {
          const cfg = await fetchSystemConfig();
          setSystemConfig(cfg);
          if (!cfg?.secondary_currency_code) {
            setExchangeRates([]);
            setExchangeRateId(null);
            return;
          }
          const rates = await fetchExchangeRates();
          setExchangeRates(rates);
          setExchangeRateId((prev) =>
            prev && rates.some((r) => Number(r.id) === Number(prev)) ? prev : rates[0]?.id ?? null
          );
        } catch {
          /* ignore */
        }
      })();
    };
    window.addEventListener(EXCHANGE_RATES_UPDATED_EVENT, onFxUpdated);
    return () => window.removeEventListener(EXCHANGE_RATES_UPDATED_EVENT, onFxUpdated);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get("/vendors");
        if (cancelled) return;
        setVendors(Array.isArray(res.data) ? res.data : []);
      } catch {
        setVendors([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isNew) {
      setLines([]);
      setPoCreatedAt(null);
      setLoadingPo(false);
      return;
    }
    let cancelled = false;
    setLoadingPo(true);
    (async () => {
      try {
        const cfg = await fetchSystemConfig();
        const dual = Boolean(cfg?.secondary_currency_code);
        const res = await apiClient.get(`/purchase-orders/${id}`);
        if (cancelled) return;
        const po = res.data;
        setVendorId(po.vendor_id);
        setReference(po.reference || "");
        setStatus(po.status || "draft");
        setPoCreatedAt(po.created_at || null);
        const mapped =
          (po.items || []).map((it) => ({
            key: `k-${it.id}`,
            product_id: it.product_id,
            quantity: Number(it.quantity),
            single_price: Number(it.single_price),
            label: it.product ? formatProductLabel(it.product) : String(it.product_id),
          })) || [];
        setLines(mapped);

        if (dual && (po.amount_paid_main != null || po.amount_paid_secondary != null || po.exchange_rate_id)) {
          setAmountPaidMain(toApiNumber(po.amount_paid_main, po.amount_paid ?? 0));
          setAmountPaidSecondary(toApiNumber(po.amount_paid_secondary, 0));
          if (po.exchange_rate_id) setExchangeRateId(po.exchange_rate_id);
        } else {
          setAmountPaid(toApiNumber(po.amount_paid, 0));
          setAmountPaidMain(toApiNumber(po.amount_paid, 0));
          setAmountPaidSecondary(0);
        }
      } catch (e) {
        message.error(e?.response?.data?.message || t("purchaseOrdersLoadFailed", "Failed to load orders."));
        navigate("/purchase-orders");
      } finally {
        if (!cancelled) setLoadingPo(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formatProductLabel, id, isNew, navigate, t]);

  const runProductSearch = useCallback(
    async (q) => {
      const term = String(q || "").trim();
      if (term.length < 1) {
        setSearchOptions([]);
        return;
      }
      setSearchFetching(true);
      try {
        const res = await apiClient.get("/products/search-for-po", { params: { q: term, limit: 30 } });
        const list = Array.isArray(res.data) ? res.data : [];
        setSearchOptions(
          list.map((p) => ({
            value: p.id,
            label: formatProductLabel(p),
            product: p,
          }))
        );
      } catch {
        setSearchOptions([]);
      } finally {
        setSearchFetching(false);
      }
    },
    [formatProductLabel]
  );

  const onSearchProducts = useCallback(
    (q) => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => runProductSearch(q), 280);
    },
    [runProductSearch]
  );

  const addLineFromProduct = useCallback(
    (product) => {
      if (!product?.id) return;
      setLines((prev) => [
        ...prev,
        {
          key: newLineKey(),
          product_id: product.id,
          quantity: 1,
          single_price: toApiNumber(product.price, 0),
          label: formatProductLabel(product),
        },
      ]);
      setProductSelectKey((k) => k + 1);
    },
    [formatProductLabel]
  );

  const patchLine = useCallback((key, patch) => {
    setLines((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }, []);

  const removeLine = useCallback((key) => {
    setLines((prev) => prev.filter((r) => r.key !== key));
  }, []);

  const handleSave = async () => {
    const validLines = lines.filter((r) => r.product_id != null);
    if (!vendorId) {
      message.error(t("purchaseOrdersFieldVendorPh", "Select vendor"));
      return;
    }
    if (!validLines.length) {
      message.error(t("purchaseOrdersLinesRequired", "At least one product line is required."));
      return;
    }

    const secVal = toMoney(amountPaidSecondary);
    if (useDualCurrency && secVal > 0 && !exchangeRateId) {
      message.error(t("poSelectExchangeRate", "Select exchange rate when using secondary currency."));
      return;
    }

    const payload = {
      vendor_id: vendorId,
      reference: reference || null,
      status: status || undefined,
      lines: validLines.map((r) => ({
        product_id: Number(r.product_id),
        quantity: Number(r.quantity),
        single_price: toMoney(r.single_price),
      })),
    };

    if (useDualCurrency) {
      payload.amount_paid_main = toMoney(amountPaidMain);
      payload.amount_paid_secondary = secVal;
      if (secVal > 0) payload.exchange_rate_id = exchangeRateId;
    } else {
      payload.amount_paid = toMoney(amountPaid);
    }

    setSaving(true);
    try {
      if (isNew) {
        await apiClient.post("/purchase-orders", payload);
        message.success(t("purchaseOrdersCreated", "Order created."));
      } else {
        await apiClient.put(`/purchase-orders/${id}`, payload);
        message.success(t("purchaseOrdersUpdated", "Order updated."));
      }
      navigate("/purchase-orders");
    } catch (e) {
      message.error(e?.response?.data?.message || t("purchaseOrdersSaveFailed", "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  const lineColumns = [
    {
      title: t("purchaseOrdersLineProduct", "Product"),
      key: "label",
      render: (_, row) => <span>{row.label || `#${row.product_id}`}</span>,
    },
    {
      title: t("purchaseOrdersLineQty", "Qty"),
      key: "quantity",
      width: 96,
      render: (_, row) => (
        <InputNumber
          min={1}
          step={1}
          size="small"
          style={{ width: "100%" }}
          value={row.quantity}
          onChange={(v) => patchLine(row.key, { quantity: v ?? 1 })}
        />
      ),
    },
    {
      title: t("purchaseOrdersLinePrice", "Unit Price"),
      key: "single_price",
      width: 120,
      render: (_, row) => (
        <InputNumber
          min={0}
          size="small"
          style={{ width: "100%" }}
          value={row.single_price}
          onChange={(v) => patchLine(row.key, { single_price: v ?? 0 })}
        />
      ),
    },
    {
      title: t("purchaseOrdersLineSubtotal", "Line Total"),
      key: "sub",
      width: 120,
      render: (_, row) =>
        formatCurrency(toApiNumber(row.quantity, 0) * toApiNumber(row.single_price, 0), mainCode),
    },
    {
      title: "",
      key: "x",
      width: 40,
      render: (_, row) => (
        <Button type="text" danger size="small" icon={<MinusCircleOutlined />} onClick={() => removeLine(row.key)} />
      ),
    },
  ];

  const metaCard = (
    <Card size="small" className="po-meta-card" title={t("poCompactMetaTitle", "Order Info")}>
      <Row gutter={[12, 10]}>
        <Col xs={24} md={8}>
          <Text type="secondary" className="po-field-label">
            {t("purchaseOrdersFieldVendor", "Vendor")}
          </Text>
          <Select
            size="small"
            style={{ width: "100%" }}
            showSearch
            optionFilterProp="label"
            placeholder={t("purchaseOrdersFieldVendorPh", "Select vendor")}
            value={vendorId}
            onChange={setVendorId}
            options={vendors.map((v) => ({
              value: v.id,
              label: `${v.name} (${formatCurrency(v.balance_main, mainCode)})`,
            }))}
          />
        </Col>
        <Col xs={24} md={8}>
          <Text type="secondary" className="po-field-label">
            {t("purchaseOrdersFieldReference", "Reference / Document")}
          </Text>
          <Input size="small" value={reference} onChange={(e) => setReference(e.target.value)} />
        </Col>
        <Col xs={24} md={8}>
          <Text type="secondary" className="po-field-label">
            {t("purchaseOrdersFieldStatus", "Status")}
          </Text>
          <Select
            size="small"
            style={{ width: "100%" }}
            value={status}
            onChange={setStatus}
            options={["draft", "ordered", "received", "cancelled"].map((s) => ({
              value: s,
              label: t(`poStatus_${s}`),
            }))}
          />
          <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
            {t("poStatusWorkflowHint", "Draft: no vendor balance or stock. Ordered: vendor balance starts, stock unchanged. Received: same balance + items enter stock. Payments below only record money paid to vendor.")}
          </Text>
        </Col>
      </Row>
    </Card>
  );

  const productsCard = (
    <Card size="small" className="po-card-products" title={t("poCardProductsTitle", "Lines & Products")}>
      <Space direction="vertical" size={10} style={{ width: "100%" }}>
        <div className="po-search-row">
          <Text strong style={{ fontSize: 13 }}>
            {t("poSearchProductLabel", "Search & Add Product")}
          </Text>
          <Select
            key={productSelectKey}
            showSearch
            allowClear
            size="small"
            filterOption={false}
            loading={searchFetching}
            placeholder={t("poSearchProductPlaceholder", "SKU, barcode, or name…")}
            style={{ width: "100%", marginTop: 6 }}
            options={searchOptions}
            onSearch={onSearchProducts}
            onChange={(pid) => {
              const hit = searchOptions.find((o) => Number(o.value) === Number(pid));
              if (hit?.product) addLineFromProduct(hit.product);
            }}
            notFoundContent={searchFetching ? <Spin size="small" /> : null}
          />
        </div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t("poSearchHint", "Type and pause to see results; selecting adds to list.")}
        </Text>
        <Table
          className="po-products-table"
          rowKey="key"
          size="small"
          pagination={false}
          columns={lineColumns}
          dataSource={lines}
          locale={{ emptyText: t("poLinesEmpty", "No lines — search for a product and add it.") }}
          scroll={{ x: 520 }}
        />
        <div style={{ textAlign: "right" }}>
          <Text type="secondary">{t("purchaseOrdersComputedTotal", "Computed Total (from lines)")}: </Text>
          <Text strong>{formatCurrency(lineTotals, mainCode)}</Text>
        </div>
      </Space>
    </Card>
  );

  const paymentCard = (
    <Card size="small" className="po-card-payment" title={t("poPaymentSection", "Payment")}>
      {useDualCurrency ? (
        <Space direction="vertical" style={{ width: "100%" }} size={10}>
          <div>
            <Text type="secondary" className="po-field-label">
              {t("poAmountPaidMain", "Amount Paid ({{code}})", { code: mainCode })}
            </Text>
            <InputNumber
              min={0}
              size="small"
              style={{ width: "100%", marginTop: 4 }}
              value={amountPaidMain}
              onChange={(v) => setAmountPaidMain(v ?? 0)}
            />
          </div>
          <div>
            <Text type="secondary" className="po-field-label">
              {t("poAmountPaidSecondary", "Amount Paid in secondary currency ({{code}})", { code: secondaryCode })}
            </Text>
            <InputNumber
              min={0}
              size="small"
              style={{ width: "100%", marginTop: 4 }}
              value={amountPaidSecondary}
              onChange={(v) => setAmountPaidSecondary(v ?? 0)}
            />
          </div>
          {exchangeRates.length > 0 ? (
            <div>
              <Text type="secondary" className="po-field-label">
                {t("exchangeRate", "Exchange rate")}
              </Text>
              <Select
                size="small"
                style={{ width: "100%", marginTop: 4 }}
                placeholder={t("selectRate", "Select rate")}
                value={exchangeRateId}
                onChange={setExchangeRateId}
                options={exchangeRates.map((r) => ({
                  value: r.id,
                  label: `${String(r.effective_from || "").slice(0, 10)} — 1 ${mainCode} = ${r.main_per_secondary} ${secondaryCode}`,
                }))}
              />
            </div>
          ) : null}
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("paymentTotalApplied", "Total applied")} ({mainCode}): <strong>{formatCurrency(appliedMainTotal, mainCode)}</strong>
          </Text>
        </Space>
      ) : (
        <div>
          <Text type="secondary" className="po-field-label">
            {t("purchaseOrdersFieldPaid", "Amount Paid")}
          </Text>
          <InputNumber
            min={0}
            size="small"
            style={{ width: "100%", marginTop: 4 }}
            value={amountPaid}
            onChange={(v) => setAmountPaid(v ?? 0)}
          />
        </div>
      )}
    </Card>
  );

  const mainWorkArea = sideBySide ? (
    <Row gutter={[14, 14]} className="po-editor-main-row">
      <Col xs={24} lg={15}>
        {productsCard}
      </Col>
      <Col xs={24} lg={9}>
        <Affix offsetTop={12}>
          <div style={{ width: "100%" }}>{paymentCard}</div>
        </Affix>
      </Col>
    </Row>
  ) : (
    <Tabs
      className="po-editor-tabs"
      size="small"
      defaultActiveKey="lines"
      items={[
        {
          key: "lines",
          label: t("poTabProducts", "Products"),
          children: productsCard,
        },
        {
          key: "pay",
          label: t("poTabPayment", "Payment"),
          children: paymentCard,
        },
      ]}
    />
  );

  const printLines = lines.filter((r) => r.product_id != null);

  return (
    <div className="list-page po-editor-page">
      <div className="po-editor-screen-wrap no-print-po">
      <div className="po-editor-top">
        <div className="po-editor-top-main">
          <Link to="/purchase-orders">
            <Button type="text" size="small" icon={<ArrowLeftOutlined />} aria-label={t("poBackToList", "Back to List")} />
          </Link>
          <div className="po-editor-title-wrap">
            <Text type="secondary" className="po-editor-crumb">
              <Link to="/purchase-orders">{t("purchaseOrdersTitle", "Purchase Orders")}</Link>
            </Text>
            <Title level={4} style={{ margin: "4px 0 0", fontSize: "1.1rem" }}>
              {isNew ? t("purchaseOrdersCreateTitle", "New Purchase Order") : `${t("purchaseOrdersEditTitle", "Edit Purchase Order")} #${id}`}
            </Title>
          </div>
        </div>
      </div>

      <Spin spinning={loadingPo}>
        {metaCard}
        {mainWorkArea}
      </Spin>

      <div className="po-editor-footer">
        <Space wrap>
          <Button
            size="large"
            icon={<PrinterOutlined />}
            onClick={() => window.print()}
            disabled={!canPrint || loadingPo}
          >
            {t("print", "Print")}
          </Button>
          <Button type="primary" size="large" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
            {t("purchaseOrdersSave", "Save")}
          </Button>
        </Space>
      </div>
      </div>

      <PurchaseOrderPrintSheet
        systemConfig={systemConfig || readCachedSystemConfig()}
        t={t}
        poId={isNew ? null : id}
        isPreview={isNew}
        vendorName={vendorDisplayName}
        reference={reference}
        status={status}
        poCreatedAt={poCreatedAt}
        lines={printLines}
        lineTotals={lineTotals}
        appliedMainTotal={appliedMainTotal}
        useDualCurrency={useDualCurrency}
        mainCode={mainCode}
        secondaryCode={secondaryCode}
        amountPaidMain={amountPaidMain}
        amountPaidSecondary={amountPaidSecondary}
        selectedRate={selectedRate}
      />
    </div>
  );
};

export default PurchaseOrderEditorPage;
